import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const Shipping = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState({ page: 1, total: 0, total_pages: 0 });
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ order_id: '', courier_name: '', awb_number: '', tracking_url: '', status: 'pending', shipped_at: '', delivered_at: '', ndr_status: 'none' });
  const [orders, setOrders] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [orderFilterPlatform, setOrderFilterPlatform] = useState('');

  const fetchShipments = async (p = 1) => {
    try {
      setLoading(true);
      const url = `/shipping/list.php?page=${p}&limit=50${filter ? '&marketplace=' + filter : ''}`;
      const res = await api.get(url);
      if (res.data.success) {
        setShipments(res.data.data.shipments || []);
        setPage({ page: res.data.data.page, total: res.data.data.total, total_pages: res.data.data.total_pages });
      } else setError(res.data.message);
    } catch (e) { setError('Failed to load shipments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchShipments(); api.get('/platforms/list.php').then(r => { if (r.data.success) setPlatforms(r.data.data || []); }).catch(() => {}); }, [filter]);

  useEffect(() => {
    const url = `/orders/list.php?page=1&limit=100${orderFilterPlatform ? '&marketplace=' + orderFilterPlatform : ''}`;
    api.get(url).then(r => { if (r.data.success) setOrders(r.data.data.orders || []); }).catch(() => {});
  }, [orderFilterPlatform]);

  const openCreate = () => { setEditing(null); setForm({ order_id: '', courier_name: '', awb_number: '', tracking_url: '', status: 'pending', shipped_at: '', delivered_at: '', ndr_status: 'none' }); setShowForm(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ order_id: s.order_id || '', courier_name: s.courier_name || '', awb_number: s.awb_number || '', tracking_url: s.tracking_url || '', status: s.ship_status || 'pending', shipped_at: s.shipped_at ? s.shipped_at.split(' ')[0] : '', delivered_at: s.delivered_at ? s.delivered_at.split(' ')[0] : '', ndr_status: s.ndr_status || 'none' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, shipped_at: form.shipped_at ? form.shipped_at + ' 00:00:00' : null, delivered_at: form.delivered_at ? form.delivered_at + ' 00:00:00' : null };
      if (editing) { await api.post('/shipping/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/shipping/create.php', payload); }
      setShowForm(false); fetchShipments(page.page);
    } catch (e) { setError('Failed to save shipment'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shipment?')) return;
    try { await api.post('/shipping/delete.php', { id }); fetchShipments(page.page); }
    catch (e) { setError('Failed to delete shipment'); }
  };

  const statusColor = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-800', picked_up: 'bg-blue-100 text-blue-800', in_transit: 'bg-purple-100 text-purple-800', out_for_delivery: 'bg-indigo-100 text-indigo-800', delivered: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800', returned: 'bg-gray-100 text-gray-800' };
    return map[s?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const typeColors = { amazon: 'bg-orange-100 text-orange-800', flipkart: 'bg-blue-100 text-blue-800', meesho: 'bg-purple-100 text-purple-800' };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading && !shipments.length) return <div className="text-center py-20 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Shipping</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Total: {page.total} shipments</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">Add Shipment</button>
      </div>

      <div className="flex gap-2 items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform:</label>
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(p => ({...p, page: 1})); }} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
          <option value="">All Platforms</option>
          <option value="amazon">Amazon</option>
          <option value="flipkart">Flipkart</option>
          <option value="meesho">Meesho</option>
        </select>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWB</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipped</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {shipments.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-500">No shipments found.</td></tr>
              ) : shipments.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${typeColors[s.marketplace_type] || 'bg-gray-100'}`}>{s.marketplace_type || 'N/A'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm font-mono font-medium text-blue-600">{s.awb_number || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{s.marketplace_order_id || `#${s.order_id}`}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{s.courier_name || 'N/A'}</td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(s.ship_status)}`}>{s.ship_status?.replace('_', ' ')?.toUpperCase() || 'PENDING'}</span></td>
                  <td className="px-6 py-4 text-sm text-gray-600">{fmtDate(s.shipped_at)}</td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => openEdit(s)} className="text-primary-600">Edit</button>
                    <button onClick={() => handleDelete(s.id)} className="text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page.total_pages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <span className="text-sm text-gray-600">Page {page.page} of {page.total_pages}</span>
          <div className="flex gap-2">
            <button onClick={() => fetchShipments(page.page - 1)} disabled={page.page === 1} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Previous</button>
            <button onClick={() => fetchShipments(page.page + 1)} disabled={page.page === page.total_pages} className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Shipment' : 'Add Shipment'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Order *</label>
              <div className="flex gap-2 mb-2">
                <select value={orderFilterPlatform} onChange={e => setOrderFilterPlatform(e.target.value)} className="w-1/2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                  <option value="">All Platforms</option>
                  {platforms.map(pl => <option key={pl.id} value={pl.marketplace_type}>{pl.marketplace_type.charAt(0).toUpperCase() + pl.marketplace_type.slice(1)} - {pl.account_name}</option>)}
                </select>
                <select value={form.order_id} onChange={e => setForm({...form, order_id: e.target.value})} className="w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                  <option value="">Select</option>
                  {orders.map(o => <option key={o.id} value={o.id}>{o.marketplace_order_id || `#${o.id}`}</option>)}
                </select>
              </div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Courier *</label><input type="text" value={form.courier_name} onChange={e => setForm({...form, courier_name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">AWB *</label><input type="text" value={form.awb_number} onChange={e => setForm({...form, awb_number: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Tracking URL</label><input type="url" value={form.tracking_url} onChange={e => setForm({...form, tracking_url: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="pending">Pending</option><option value="picked_up">Picked Up</option><option value="in_transit">In Transit</option><option value="out_for_delivery">Out for Delivery</option><option value="delivered">Delivered</option><option value="failed">Failed</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Shipped Date</label><input type="date" value={form.shipped_at} onChange={e => setForm({...form, shipped_at: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} Shipment</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Shipping;
